import { defineStore } from 'pinia'
import { formatBookSlotTime } from 'src/utils'
import formatDate from 'src/filters/formatDate'
import { useGuestStore } from 'src/stores/guest'
import { useAuthStore } from 'src/stores/auth'
import { useCartStore } from 'src/stores/cart'
import { usePersistedStore } from 'src/stores/persisted'
import { useCommonStore } from 'src/stores/common'

export const useReserveSlotStore = defineStore('reserveSlot', {
  state: () => ({
    storePickupSlot: null,
    scheduledDeliverySlot: null,
  }),
  getters: {
    customerSPSlot(state) {
      return state.storePickupSlot
    },
    customerSPSlotId() {
      return this.customerSPSlot ? this.customerSPSlot.slotId : null
    },
    customerSPSlotBookingID() {
      return this.customerSPSlot ? this.customerSPSlot.slotBookingID : null
    },
    customerSCDSlot(state) {
      return state.scheduledDeliverySlot
    },
    customerSCDSlotId() {
      return this.customerSCDSlot ? this.customerSCDSlot.slotId : null
    },
    customerSCDSlotBookingID() {
      return this.customerSCDSlot ? this.customerSCDSlot.slotBookingID : null
    },
    formatBookedSlot() {
      return (slotType) => {
        const commonStore = useCommonStore()
        let targetSlot = this[`customer${slotType.toUpperCase()}Slot`]
        if (targetSlot && commonStore.checkSlotBookingRequired(slotType, true))
          return `${formatDate(
            targetSlot.bookingDate,
            'ddd, MMM D'
          )} - ${formatBookSlotTime(targetSlot.slotTime)}`

        return null
      }
    },
  },
  actions: {
    async fetchAvailableSlot(params = {}) {
      const persistedStore = usePersistedStore()

      let fromDate = new Date(),
        toDate = new Date()

      toDate.setDate(fromDate.getDate() + 6)

      try {
        let { data } = await this.$axiosInstance.get('/AvailableSlot', {
          params: {
            locationID: persistedStore.currentLocationId,
            fromDate: formatDate(fromDate, 'YYYY-MM-DD'),
            toDate: formatDate(toDate, 'YYYY-MM-DD'),
            ...params,
          },
        })

        return data
      } catch (error) {}
    },
    async getCustomerBlockedSlots() {
      const guestStore = useGuestStore(),
        authStore = useAuthStore(),
        persistedStore = usePersistedStore()

      try {
        let guestCartNumber = guestStore.guestCartNumber,
          isLoggedIn = authStore.isLoggedIn
        let params = {
          locationID: persistedStore.currentLocationId,
          global: true,
        }

        if (!isLoggedIn) {
          let pickupSlotBookingID = guestStore.slotBookingId('sp'),
            deliverySlotBookingID = guestStore.slotBookingId('scd')
          if (guestCartNumber) params.orderNumber = guestCartNumber
          if (pickupSlotBookingID)
            params.pickupSlotBookingID = pickupSlotBookingID
          if (deliverySlotBookingID)
            params.deliverySlotBookingID = deliverySlotBookingID
        }

        let { data } = await this.$axiosInstance.get(
          '/AvailableSlot/CustomerBlockedSlots',
          { params }
        )

        let { storePickupSlot, scheduledDeliverySlot } = data.data

        this.setSelectedSlot({
          key: 'storePickupSlot',
          val: storePickupSlot,
        })

        this.setSelectedSlot({
          key: 'scheduledDeliverySlot',
          val: scheduledDeliverySlot,
        })

        // store slotBookingID for guest checkout
        if (!isLoggedIn) {
          guestStore.setSelectedSlotId({
            key: 'storePickupSlotId',
            val: storePickupSlot?.slotBookingID,
          })

          guestStore.setSelectedSlotId({
            key: 'scheduledDeliverySlotId',
            val: scheduledDeliverySlot?.slotBookingID,
          })
        }
      } catch (error) {}
    },
    async bookOrUpdateSlot(body, options = {}) {
      try {
        const guestStore = useGuestStore(),
          authStore = useAuthStore(),
          cartStore = useCartStore(),
          persistedStore = usePersistedStore()

        let response,
          isLoggedIn = authStore.isLoggedIn,
          selectedFulfillmentCodeName =
            persistedStore.selectedFulfillmentCodeName,
          guestSlotBookingId = guestStore.slotBookingId(
            selectedFulfillmentCodeName
          )

        if (isLoggedIn)
          response = await this.$axiosInstance.post('/AvailableSlot/Book', body)
        else {
          if (guestSlotBookingId) {
            response = await this.$axiosInstance.put(
              `/AvailableSlot/Book/${guestSlotBookingId}`,
              body
            )
          } else {
            response = await this.$axiosInstance.post(
              '/AvailableSlot/Book',
              body
            )
          }
        }

        let { storePickupSlot, scheduledDeliverySlot } = response.data.data

        if (storePickupSlot) {
          this.setSelectedSlot({
            key: 'storePickupSlot',
            val: storePickupSlot,
          })
          if (!isLoggedIn) {
            guestStore.setSelectedSlotId({
              key: 'storePickupSlotId',
              val: storePickupSlot?.slotBookingID,
            })
          }
        }

        if (scheduledDeliverySlot) {
          this.setSelectedSlot({
            key: 'scheduledDeliverySlot',
            val: scheduledDeliverySlot,
          })

          if (!isLoggedIn) {
            guestStore.setSelectedSlotId({
              key: 'scheduledDeliverySlotId',
              val: scheduledDeliverySlot?.slotBookingID,
            })
          }
        }

        await cartStore.getCartDetail({ additionalParams: options })

        return response.data
      } catch (error) {
        return error
      }
    },
    async resetSlots() {
      const guestStore = useGuestStore(),
        authStore = useAuthStore()
      let isLoggedIn = authStore.isLoggedIn
      this.storePickupSlot = null
      this.scheduledDeliverySlot = null
      if (!isLoggedIn) guestStore.resetSlots()
    },
    setSelectedSlot({ key, val }) {
      if (val) this[key] = Object.assign({}, val)
      else this[key] = null
    },
  },
})
